import { Flex, Text } from "@wayflyer/flyui";
import { useLayoutEffect, useRef } from "react";

import styles from "./months-coverage-bar.module.css";
import { planSegments } from "./plan-segments";

import type { MonthCoverage, MonthCoverageFill } from "../utils";

type MonthsCoverageBarProps = {
  months: MonthCoverage[];
  animated?: boolean;
};

const FILL_DURATION_MS = 150;
const FIRST_LOAD_DELAY_MS = 300;

const prefersNoMotion = () =>
  document.documentElement.dataset.flyuiSkipAnimations === "true" ||
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const FILL_CLASS_NAME: Record<MonthCoverageFill, string> = {
  none: styles.unfilled,
  "half-start": styles.halfStart,
  "half-end": styles.halfEnd,
  full: "",
  reviewed: styles.reviewed,
  "half-start-reviewed": styles.halfStartReviewed,
  "half-end-reviewed": styles.halfEndReviewed,
};

export const MonthsCoverageBar = ({ months, animated = false }: MonthsCoverageBarProps) => {
  const fills = months.map(({ fill }) => fill);
  const fillElements = useRef<(HTMLDivElement | null)[]>([]);
  const previousFills = useRef<MonthCoverageFill[] | null>(null);
  const sequenceEndsAt = useRef(0);

  // Runs every render: it only schedules animations when a fill actually changed.
  useLayoutEffect(() => {
    const previous = previousFills.current;
    previousFills.current = fills;
    if (previous?.join() === fills.join() || !animated || prefersNoMotion()) {
      return;
    }

    // Months already scheduled keep their place; new ones start after the last of them.
    const now = performance.now();
    if (previous === null) {
      sequenceEndsAt.current = now + FIRST_LOAD_DELAY_MS;
    }
    planSegments(fills, previous).forEach(({ changed, from, origin }, index) => {
      const element = fillElements.current[index];
      if (!element || (previous !== null && previous[index] === fills[index])) {
        return;
      }
      const interrupted = element.getAnimations();
      interrupted.forEach((animation) => animation.cancel());
      if (!changed) {
        return;
      }
      const start = Math.max(sequenceEndsAt.current, now);
      element.animate(
        [
          { transform: `scaleX(${interrupted.length > 0 ? 0 : from})`, transformOrigin: origin },
          { transform: "scaleX(1)", transformOrigin: origin },
        ],
        { duration: FILL_DURATION_MS, delay: start - now, easing: "linear", fill: "backwards" }
      );
      sequenceEndsAt.current = start + FILL_DURATION_MS;
    });
  });

  return (
    <Flex direction="column" gap="3">
      <Flex gap="var(--space-component-xxxs)">
        {fills.map((fill, index) => (
          <div key={index} className={styles.segment}>
            <div
              ref={(element) => {
                fillElements.current[index] = element;
              }}
              className={`${styles.fill} ${FILL_CLASS_NAME[fill]}`}
            />
          </div>
        ))}
      </Flex>
      <Flex gap="var(--space-component-xxxs)">
        {months.map((month, index) => (
          <div key={index} className={styles.label}>
            <Text size="xs" color="subtle">
              {month.label}
            </Text>
          </div>
        ))}
      </Flex>
    </Flex>
  );
};
